import type { Country } from 'react-phone-number-input';
import type {
  LocalisationPolicyResponse,
  LocalisationRuntimePolicy,
  LocalisationRuntimePolicyOk,
} from 'types/localisationPolicy';

export const isLocalisationPolicyReady = (
  policy?: LocalisationPolicyResponse | null,
): policy is LocalisationPolicyResponse & {
  runtime: LocalisationRuntimePolicyOk;
} => policy?.runtime.status === 'ok';

export const selectCountryName = (
  policy?: LocalisationPolicyResponse | null,
): string | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.countryIdentity.countryName
    : undefined;

export const selectPhoneIso = (
  policy?: LocalisationPolicyResponse | null,
): Country | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.contact.phoneIso
    : undefined;

export const selectCallingCode = (
  policy?: LocalisationPolicyResponse | null,
): string | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.contact.callingCode
    : undefined;

// Australia's calling code, read from the policy's generated country list.
// Australia is the legacy default numbers were historically stored under, so
// this is the source prefix the phone rewrite strips — sourced from the policy
// (not a `+61` literal) so the value lives in one place. `generated` is always
// present, so this resolves even when the tenant's runtime policy is missing.
export const selectAustralianCallingCode = (
  policy?: LocalisationPolicyResponse | null,
): string | undefined =>
  policy?.generated.countries.find((country) => country.countryCode === 'AU')
    ?.contact.callingCode;

export const selectBusinessIdentifierLabel = (
  policy?: LocalisationPolicyResponse | null,
): string | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.businessIdentifier.label
    : undefined;

export const selectBusinessIdentifierType = (
  policy?: LocalisationPolicyResponse | null,
): string | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.businessIdentifier.type
    : undefined;

export const selectBusinessIdentifierFieldLabel = (
  policy?: LocalisationPolicyResponse | null,
): string | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.businessIdentifier.fieldLabel
    : undefined;

export const selectBusinessIdentifierSearchUrl = (
  policy?: LocalisationPolicyResponse | null,
  fallbackCountryName = '',
): string | undefined => {
  if (isLocalisationPolicyReady(policy)) {
    return policy.runtime.businessIdentifier.searchUrl;
  }

  if (!fallbackCountryName) return undefined;

  return policy?.generated.countries.find(
    (country) => country.countryName === fallbackCountryName,
  )?.businessIdentifier.searchUrl;
};

export const selectClientBusinessIdentifierFieldLabel = (
  policy?: LocalisationPolicyResponse | null,
): string | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.businessIdentifier.clientFieldLabel
    : undefined;

export const selectSubdivisionLabel = (
  policy?: LocalisationPolicyResponse | null,
): string | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.address.subdivisionLabel
    : undefined;

const fallbackGeneratedCountry = (
  policy: LocalisationPolicyResponse | null | undefined,
  fallbackCountryName: string,
) =>
  fallbackCountryName
    ? policy?.generated.countries.find(
        (country) => country.countryName === fallbackCountryName,
      )
    : undefined;

export const selectPostalCodeLabel = (
  policy?: LocalisationPolicyResponse | null,
): string | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.address.postalCodeLabel
    : undefined;

export const selectPostalCodePattern = (
  policy?: LocalisationPolicyResponse | null,
  fallbackCountryName = '',
): string | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.address.postalCodePattern
    : fallbackGeneratedCountry(policy, fallbackCountryName)?.address
        .postalCodePattern;

export const selectPostalCodeExample = (
  policy?: LocalisationPolicyResponse | null,
  fallbackCountryName = '',
): string | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.address.postalCodeExample
    : fallbackGeneratedCountry(policy, fallbackCountryName)?.address
        .postalCodeExample;

export const selectPlacesCountryRestrictions = (
  policy?: LocalisationPolicyResponse | null,
): [string] | undefined => {
  if (!isLocalisationPolicyReady(policy)) return undefined;
  const [countryRestriction] = policy.runtime.address.placesCountryRestrictions;
  return countryRestriction ? [countryRestriction] : undefined;
};

export const selectEffectiveTimezone = (
  policy?: LocalisationPolicyResponse | null,
): string | undefined =>
  isLocalisationPolicyReady(policy)
    ? policy.runtime.timezone.effective
    : undefined;

// `value` is the API-contract string (always the AU concept label, e.g.
// 'GST free expense') that the backend expects; `label` is the per-country
// display string (e.g. 'Zero-rated expense' for NZ). Keep `value` stable and
// localise only `label` — swapping them would break API calls for NZ tenants.
const TAX_DISPLAY_CONCEPT_VALUES: Record<string, string> = {
  GST_ON_EXPENSE: 'GST on expense',
  GST_FREE_EXPENSE: 'GST free expense',
  BAS_EXCLUDED: 'BAS excluded',
};

export const selectLineItemTaxDisplayOptions = (
  policy?: LocalisationPolicyResponse | null,
): Array<{ value: string; label: string }> | undefined => {
  if (!isLocalisationPolicyReady(policy)) return undefined;

  const countryPolicy = policy.generated.countries.find(
    (country) =>
      country.countryCode === policy.runtime.countryIdentity.countryCode,
  );

  const options = countryPolicy?.taxDisplay.lineItemOptions;
  if (!options?.length) return undefined;

  return options.map((option) => ({
    value: TAX_DISPLAY_CONCEPT_VALUES[option.concept] ?? option.label,
    label: option.label,
  }));
};

export const isMissingLocalisationPolicy = (
  runtime?: LocalisationRuntimePolicy | null,
): boolean => runtime?.status === 'missing_policy';
